import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { getOnboardingResumeTarget, readPendingInviteFromSession, hasRealOrgId } from '@/utils/onboardingResume';
import Button from '@components/ui/Button';
import { RiAlertLine, RiMailLine } from "@remixicon/react";
import VerifySplash from '@components/auth/VerifySplash';
import CrossDeviceRecovery from '@components/auth/CrossDeviceRecovery';

/**
 * After Supabase confirms the email, decide where the user actually
 * belongs. Previously this hardcoded /mfa-enroll — which was wrong for
 * users who had NO BE profile yet (MFAEnroll would 404/403 on its
 * profile lookups) and for OAuth users who might not have completed
 * onboarding. Now every landing routes through the resume helper.
 */
export default function EmailConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;

    // Resolve the correct destination for the freshly-signed-in
    // Supabase user by querying BE state — matches the resume logic
    // used by SignIn / OAuth callbacks so every entry point agrees.
    // Preserves any signup-context params on the URL (?d=...) when
    // handing off to /onboarding so the Profile step can prefill.
    const routeAfterConfirm = async (session) => {
      if (!mounted) return;
      try {
        const userId = session?.user?.id;
        let profile = null;
        let organization = null;
        if (userId) {
          profile = await apiClient.getUserByUserId(userId).catch(() => null);
          if (hasRealOrgId(profile)) {
            organization = await apiClient.getOrganizationById(profile.org_id).catch(() => null);
          }
        }
        const target = getOnboardingResumeTarget(profile, organization, {
          pendingInvite: readPendingInviteFromSession(session),
        });
        // Preserve the ?d=... param the SignUp step encoded into
        // emailRedirectTo so Onboarding can prefill firstName /
        // lastName / userType without asking again on this device.
        const d = searchParams.get('d');
        let dest = target.path;
        if (dest === '/onboarding' && d) {
          dest = `/onboarding?d=${encodeURIComponent(d)}`;
        }
        if (mounted) navigate(dest);
      } catch (err) {
        console.error('EmailConfirmation: resume routing failed', err);
        // Fail-open — if the resume lookup blows up, land on
        // /onboarding step 1 rather than dashboard (which would 403).
        if (mounted) navigate('/onboarding');
      }
    };

    const handleEmailConfirmation = async () => {
      try {
        // Supabase reports auth-flow errors in the query string
        // and (redundantly) in the URL fragment. Email link
        // scanners sometimes preserve only one, so check both.
        const queryParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const errorCode = queryParams.get('error_code') || hashParams.get('error_code');
        if (errorCode === 'otp_expired' || errorCode === 'access_denied') {
          setStatus('link_expired');
          return;
        }
        const hasCodeParam = queryParams.has('code');

        // detectSessionInUrl (configured in supabase.js) automatically handles
        // the ?code=xxx exchange via PKCE. We just listen for the result.
        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          if (event === 'SIGNED_IN' && session) {
            setStatus('success');
            setTimeout(() => routeAfterConfirm(session), 1500);
          }
        });

        authSubscription = data.subscription;

        // Check if session was already established (e.g. detectSessionInUrl processed it)
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData.session) {
          setStatus('success');
          setTimeout(() => routeAfterConfirm(sessionData.session), 1500);
        } else if (hasCodeParam) {
          // Code was in URL but no session yet — detectSessionInUrl may still be processing.
          // Wait a bit, then check again. If still no session, email was confirmed server-side
          // but the PKCE code_verifier was lost (e.g. different tab/browser).
          setTimeout(async () => {
            if (!mounted) return;
            const { data: retryData } = await supabase.auth.getSession();
            if (retryData.session) {
              setStatus('success');
              setTimeout(() => routeAfterConfirm(retryData.session), 1500);
            } else {
              // Email is confirmed server-side but session couldn't be established.
              setStatus('confirmed_no_session');
            }
          }, 2000);
        } else {
          setStatus('pending');
        }

      } catch (err) {
        console.error('Email confirmation error:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    handleEmailConfirmation();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate, searchParams]);

  // Loading + success both use the shared VerifySplash so this
  // page's visual language matches TeamRegistration's post-verify
  // splash pixel-for-pixel.
  if (status === 'verifying') {
    return (
      <VerifySplash
        title="Email Confirmation"
        message="Verifying your email…"
        variant="loading"
      />
    );
  }

  if (status === 'success') {
    return (
      <VerifySplash
        title="Email Confirmation"
        subtitle="Taking you to the next step…"
        message="Email confirmed successfully!"
        variant="success"
      />
    );
  }

  if (status === 'confirmed_no_session') {
    return (
      <CrossDeviceRecovery
        subtitle="Looks like you opened this link on a different device or browser than where you signed up. Sign in to continue."
      />
    );
  }

  // The remaining states are page-specific — keep them inline.
  return (
    <>
      <div className="text-center lg:text-left mb-6">
        <h2 className="text-3xl font-bold text-primary-500">
          {status === 'link_expired' ? 'This link is no longer valid' : 'Email Confirmation'}
        </h2>
      </div>

      {status === 'link_expired' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <RiAlertLine className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">
            Verification links expire after a few minutes and can only be
            used once. Sometimes email security scanners follow the link
            before you get a chance to.
          </p>
          <p className="text-sm text-gray-600 text-center">
            Go back to sign in — you'll be prompted to send a fresh verification email.
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}

      {status === 'pending' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiMailLine className="h-12 w-12 text-primary-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Please check your email and click the confirmation link.
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <RiAlertLine className="h-12 w-12 text-red-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">
            Error confirming email: {error}
          </p>
          <div className="pt-4">
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
